import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import Select from '../components/Select';
import RowActions from '../components/RowActions';
import ModalConfirm from '../components/ModalConfirm';
import Modal from '../components/ui/Modal';
import Field, { FormRow } from '../components/ui/Field';
import DataTable from '../components/ui/DataTable';
import Estado from '../components/ui/Estado';
import PageHeader from '../components/ui/PageHeader';
import { toast } from '../components/Toast';
import { unidadeArea } from '../utils/unidades';

const TIPOS = [
  { value: 'LAVOURA', label: 'Lavoura' },
  { value: 'ESTUFA', label: 'Estufa' },
];

const FORM_VAZIO = { descricao: '', tipoArea: 'LAVOURA', cidade: '', areaHectares: '', nomeProprietario: '' };

/** Campos compartilhados entre criar e editar — antes eram duplicados. */
const CamposArea = React.memo(function CamposArea({ dados, onChange, erroArea }) {
  const estufa = dados.tipoArea === 'ESTUFA';
  return (
    <>
      <FormRow>
        <Field label="Tipo" obrigatorio>
          <Select value={dados.tipoArea || 'LAVOURA'} onChange={v => onChange({ ...dados, tipoArea: v })} options={TIPOS} />
        </Field>
        <Field label="Nome da área" obrigatorio hint="Como você chama essa área no dia a dia">
          <input value={dados.descricao} onChange={e => onChange({ ...dados, descricao: e.target.value })}
            required placeholder={estufa ? 'Ex: Estufa 1' : 'Ex: Talhão Norte'} />
        </Field>
      </FormRow>
      <FormRow>
        <Field label="Cidade" obrigatorio>
          <input value={dados.cidade} onChange={e => onChange({ ...dados, cidade: e.target.value })} required />
        </Field>
        <Field
          label={estufa ? 'Metragem (m²)' : 'Área (hectares)'}
          obrigatorio={!estufa}
          erro={erroArea}
          hint={estufa
            ? 'Opcional. Usada apenas como referência.'
            : 'Base do cálculo de custo e produtividade por hectare.'}
        >
          <input type="number" step="0.1" min="0" value={dados.areaHectares}
            onChange={e => onChange({ ...dados, areaHectares: e.target.value })}
            required={!estufa} placeholder={estufa ? 'Ex: 500' : 'Ex: 12,5'} />
        </Field>
      </FormRow>
      <FormRow>
        <Field label="Proprietário" hint="Opcional — útil para arrendamento e imposto de renda">
          <input value={dados.nomeProprietario || ''} onChange={e => onChange({ ...dados, nomeProprietario: e.target.value })} />
        </Field>
      </FormRow>
    </>
  );
});

export default function Lavouras() {
  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [form, setForm] = useState(FORM_VAZIO);
  const [erroArea, setErroArea] = useState('');
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [erroModal, setErroModal] = useState('');
  const [excluindo, setExcluindo] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const fecharEdicao = useCallback(() => {
    setEditando(null);
    setErroModal('');
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErroCarga('');
    try {
      setLista(await api.getLavouras() || []);
    } catch (err) {
      setErroCarga(err.message || 'Não foi possível carregar as áreas de cultivo.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async (e) => {
    e.preventDefault();
    setErroArea('');
    // Hectare é a base do custo por área; sem ele o relatório fica sem sentido.
    if (form.tipoArea !== 'ESTUFA' && (!form.areaHectares || parseFloat(form.areaHectares) <= 0)) {
      setErroArea('Informe a área em hectares.');
      return;
    }
    setSalvando(true);
    try {
      await api.criarLavoura({
        ...form,
        areaHectares: parseFloat(form.areaHectares) || null,
        nomeProprietario: form.nomeProprietario || null,
      });
      setForm(FORM_VAZIO);
      setCriando(false);
      toast('Área de cultivo criada.', 'success');
      carregar();
    } catch (err) { toast(err.message, 'error'); }
    finally { setSalvando(false); }
  };

  const salvarEdicao = async (e) => {
    e?.preventDefault();
    setErroModal('');
    if (editando.tipoArea !== 'ESTUFA' && (!editando.areaHectares || parseFloat(editando.areaHectares) <= 0)) {
      setErroModal('Informe a área em hectares.');
      return;
    }
    try {
      await api.editarLavoura(editando.id, {
        ...editando,
        areaHectares: parseFloat(editando.areaHectares) || null,
        nomeProprietario: editando.nomeProprietario || null,
      });
      setEditando(null);
      toast('Área de cultivo atualizada.', 'success');
      carregar();
    } catch (err) { setErroModal(err.message); }
  };

  const confirmarExclusao = async () => {
    try {
      await api.excluirLavoura(excluindo.id);
      setExcluindo(null);
      toast('Área de cultivo excluída.', 'success');
      carregar();
    } catch (err) {
      setExcluindo(null);
      toast(err.message, 'error');
    }
  };

  return (
    <div>
      <PageHeader
        titulo="Áreas de cultivo"
        icone="lavoura"
        descricao="Onde você planta. Lavoura é medida em hectares; estufa, em m²."
        acao={{ label: 'Nova área', icone: 'adicionar', onClick: () => setCriando(true) }}
      />

      <div className="card">
        <div className="secao-titulo">
          <h2>Áreas cadastradas</h2>
          {lista.length > 0 && <span className="contador">{lista.length}</span>}
        </div>

        <Estado carregando={carregando} erro={erroCarga} aoTentarNovamente={carregar} skeleton="card" itens={4}>
          <DataTable
            linhas={lista}
            chaveDe={l => l.id}
            vazio={{
              icone: 'lavoura',
              titulo: 'Nenhuma área cadastrada',
              descricao: 'Cadastre sua primeira lavoura ou estufa. Depois você vincula ela a uma safra para começar a lançar custos e produção.',
              acao: { label: 'Nova área', onClick: () => setCriando(true) },
            }}
            colunas={[
              // No cartão o nome é o título, a área é o destaque, e o tipo vira a
              // linha de contexto.
              { chave: 'tipo', titulo: 'Tipo', papel: 'subtitulo',
                render: l => l.tipoArea === 'ESTUFA' ? 'Estufa' : 'Lavoura' },
              { chave: 'descricao', titulo: 'Nome', papel: 'titulo', render: l => l.descricao },
              { chave: 'cidade', titulo: 'Cidade' },
              { chave: 'area', titulo: 'Área', alinhar: 'direita', papel: 'destaque',
                render: l => l.areaHectares ? `${l.areaHectares} ${unidadeArea(l.tipoArea)}` : '—' },
              { chave: 'proprietario', titulo: 'Proprietário', ocultarSeVazia: true,
                valor: l => l.nomeProprietario, render: l => l.nomeProprietario || '' },
              { chave: 'acoes', titulo: 'Ações', render: l => (
                  <RowActions
                    onEdit={() => { setEditando({ ...l, areaHectares: l.areaHectares ?? '' }); setErroModal(''); }}
                    onDelete={() => setExcluindo(l)}
                  />
                ) },
            ]}
          />
        </Estado>
      </div>

      {criando && (
        <Modal titulo="Nova área de cultivo" onClose={() => setCriando(false)} largura={560}>
          <p className="secao-sub">
            <strong>Lavoura</strong> gera custo e produtividade por hectare;
            em <strong>estufa</strong> os indicadores são da estufa inteira.
          </p>
          <form onSubmit={salvar}>
            <CamposArea dados={form} onChange={setForm} erroArea={erroArea} />
            <div className="actions modal-rodape">
              <button type="button" className="btn-secondary" onClick={() => setCriando(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={salvando}>
                {salvando ? 'Salvando...' : 'Cadastrar área'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editando && (
        <Modal titulo="Editar área de cultivo" onClose={fecharEdicao} largura={560}>
          {erroModal && <div className="aviso aviso--erro">{erroModal}</div>}
          <form onSubmit={salvarEdicao}>
            <CamposArea dados={editando} onChange={setEditando} />
            <div className="actions modal-rodape">
              <button type="button" className="btn-secondary" onClick={fecharEdicao}>Cancelar</button>
              <button type="submit" className="btn-primary">Salvar</button>
            </div>
          </form>
        </Modal>
      )}

      {excluindo && (
        <ModalConfirm
          titulo="Excluir área de cultivo"
          mensagem={<>Excluir <strong>{excluindo.descricao}</strong>?</>}
          aviso="Áreas com safras, custos ou produção registrados não podem ser excluídas."
          onConfirm={confirmarExclusao}
          onCancel={() => setExcluindo(null)}
        />
      )}
    </div>
  );
}
